import { User, LoginCredentials, RegisterData, AuthResponse, GoogleLoginData, RegisterResponse } from '../types/auth';
import { API_CONFIG } from '../config/api.config';
import { createAuthenticatedApi, tokenManager } from './apiInterceptors';
import { logger } from '../utils/logger';
import { currentLanguage } from '../i18n';
import { browserTimeZone } from '../utils/timeZone';

// Sent at sign-up so the account starts with the language and time zone the user sees
const signupLocale = () => ({ language: currentLanguage(), timezone: browserTimeZone() });

// Re-export tokenManager so existing consumers (goalService, contexts, etc.) keep working
export { tokenManager };

const api = createAuthenticatedApi({
  skipRefreshFor: ['/auth/login', '/auth/register', '/auth/google', '/login', '/register', '/email/verify', '/email/verify-code'],
});

export const authService = {
  // Authentication
  // No tokens yet: the account is usable once the emailed link is opened (verifyEmail)
  register: async (userData: RegisterData): Promise<RegisterResponse> => {
    // No username (the first-goal flow, T-158): leave the key out and the server makes one from
    // the name; an empty one fails its length check
    const { username, ...rest } = userData;
    const payload = username.trim() ? userData : rest;
    const response = await api.post(API_CONFIG.ENDPOINTS.REGISTER, { ...payload, ...signupLocale() });
    return response.data;
  },

  verifyEmail: async (token: string): Promise<AuthResponse> => {
    const response = await api.post(API_CONFIG.ENDPOINTS.EMAIL_VERIFY, { token });
    const authData = response.data;
    tokenManager.setToken(authData.access_token);
    tokenManager.setRefreshToken(authData.refresh_token);
    return authData;
  },

  /** The 6-digit code from the email (T-135): verifies the address and signs in. A wrong code is
   * 400 VERIFICATION_CODE_INVALID with details.attemptsLeft; an expired or used-up one is
   * VERIFICATION_CODE_EXPIRED. */
  verifyEmailCode: async (email: string, code: string): Promise<AuthResponse> => {
    const response = await api.post(API_CONFIG.ENDPOINTS.EMAIL_VERIFY_CODE, { email, code });
    const authData = response.data;
    tokenManager.setToken(authData.access_token);
    tokenManager.setRefreshToken(authData.refresh_token);
    return authData;
  },

  resendVerificationEmail: async (email: string): Promise<void> => {
    await api.post(API_CONFIG.ENDPOINTS.EMAIL_VERIFY_RESEND, { email });
  },

  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post(API_CONFIG.ENDPOINTS.LOGIN, credentials);
    const authData = response.data;
    
    // Store tokens
    tokenManager.setToken(authData.access_token);
    tokenManager.setRefreshToken(authData.refresh_token);
    
    return authData;
  },

  loginWithGoogle: async (googleData: GoogleLoginData): Promise<AuthResponse> => {
    const response = await api.post(API_CONFIG.ENDPOINTS.GOOGLE_LOGIN, { ...googleData, ...signupLocale() });
    const authData = response.data;

    tokenManager.setToken(authData.access_token);
    tokenManager.setRefreshToken(authData.refresh_token);

    return authData;
  },

  logout: async (): Promise<void> => {
    try {
      // Send the refresh token too so the server can revoke it, not just the access token
      await api.post(API_CONFIG.ENDPOINTS.LOGOUT, { refresh_token: tokenManager.getRefreshToken() });
    } catch (error) {
      logger.error('Logout error', error);
    } finally {
      tokenManager.clearTokens();
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get(API_CONFIG.ENDPOINTS.ME);
    return response.data.user;
  },

  refreshToken: async (): Promise<string> => {
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await api.post(API_CONFIG.ENDPOINTS.REFRESH, { rotate: true }, {
      headers: { Authorization: `Bearer ${refreshToken}` }
    });
    
    const { access_token, refresh_token } = response.data;
    tokenManager.setToken(access_token);
    if (refresh_token) tokenManager.setRefreshToken(refresh_token);
    
    return access_token;
  },

  // Utility methods
  isAuthenticated: (): boolean => {
    return !!tokenManager.getToken();
  },

  // Password reset flow
  requestPasswordReset: async (email: string): Promise<void> => {
    await api.post(API_CONFIG.ENDPOINTS.PASSWORD_FORGOT, { email });
  },
  verifyResetToken: async (token: string): Promise<boolean> => {
    try {
      await api.post(API_CONFIG.ENDPOINTS.PASSWORD_RESET_VERIFY, { token });
      return true;
    } catch {
      return false;
    }
  },
  submitNewPassword: async (token: string, newPassword: string) => {
    const response = await api.post(API_CONFIG.ENDPOINTS.PASSWORD_RESET, { token, newPassword });
    const data = response.data;
    if (data.access_token && data.refresh_token) {
      tokenManager.setToken(data.access_token);
      tokenManager.setRefreshToken(data.refresh_token);
    }
    return data;
  },
  setupPassword: async (newPassword: string): Promise<AuthResponse> => {
    const response = await api.post(API_CONFIG.ENDPOINTS.PASSWORD_SETUP, { newPassword });
    const authData = response.data;
    if (authData.access_token && authData.refresh_token) {
      tokenManager.setToken(authData.access_token);
      tokenManager.setRefreshToken(authData.refresh_token);
    }
    return authData;
  },

  // Profile management
  updateProfile: async (data: {
    firstName?: string;
    lastName?: string;
    themePreference?: string;
    language?: string;
    timezone?: string;
  }): Promise<User> => {
    const response = await api.put(API_CONFIG.ENDPOINTS.PROFILE_UPDATE, data);
    return response.data.user;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    const response = await api.post(API_CONFIG.ENDPOINTS.PASSWORD_CHANGE, { currentPassword, newPassword });
    // Changing the password signs out every session; keep this one with the fresh pair
    const data = response.data;
    if (data.access_token && data.refresh_token) {
      tokenManager.setToken(data.access_token);
      tokenManager.setRefreshToken(data.refresh_token);
    }
  },

  /** A copy of everything in the account as a JSON file, saved by the browser (T-170). */
  exportAccount: async (): Promise<void> => {
    const response = await api.get(API_CONFIG.ENDPOINTS.ACCOUNT_EXPORT, { responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] || '';
    const named = /filename="([^"]+)"/.exec(disposition);
    const filename = named ? named[1] : `riselio-export-${new Date().toISOString().slice(0, 10)}.json`;
    const blob = response.data instanceof Blob ? response.data : new Blob([JSON.stringify(response.data)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Some browsers read the URL after click() returns
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // Account deletion
  deleteAccount: async (
    credentials: { password?: string; idToken?: string },
    reason?: string,
  ): Promise<{ message: string; deletedAt: string }> => {
    const payload: { password?: string; idToken?: string; reason?: string } = { reason };
    if (credentials.password) {
      payload.password = credentials.password;
    }
    // Fresh Google sign-in proving the owner is present (Google-linked accounts)
    if (credentials.idToken) {
      payload.idToken = credentials.idToken;
    }

    const response = await api.post(API_CONFIG.ENDPOINTS.ACCOUNT_DELETE, payload);
    tokenManager.clearTokens();
    return response.data;
  },
};
